/**
 * The audit log's contains-search, over the entries already loaded.
 *
 * It runs in the BROWSER, and that is the whole design. The equivalent server
 * filter is an unindexed substring scan across every column of the largest
 * table in the system -- the one thing this screen has refused from the start
 * (design D2). Searching what has been fetched costs nothing, narrows exactly
 * the rows the reader is looking at, and keeps working as they load more.
 *
 * "Every field" means every field the ROW SHOWS, in the form it shows it: the
 * reader types what is on their screen. So the timestamp is matched as
 * rendered (which is why the clock mode is a parameter -- searching `14:05`
 * must find the row that displays `14:05`), the outcome matches both its pill
 * label and its stored code, and a background job is findable by the words the
 * source cell actually prints.
 *
 * Matching is case-insensitive and whitespace-separated: every term must
 * appear SOMEWHERE in the row, in any order and any field. That makes
 * `denied cem` find the denied entry touching a CEM without the reader having
 * to know which column either word lives in.
 */

import type { AuditLogEntrySummary } from '@/api/client';

import { formatTimestamp, type TimeZoneMode } from './auditTime';
import { NON_REQUEST_SENTINEL } from './payloadView';

/** How the source cell renders a non-request entry, and so how it is searched. */
const BACKGROUND_JOB = 'background job';

const OUTCOME_LABEL: Record<string, string> = {
  SUCCESS: 'Success',
  DENIED: 'Denied',
  FAILED: 'Failed',
};

/**
 * Every searchable string of one row, as one lower-cased haystack.
 *
 * The id is included even though no column prints it in full: an investigation
 * often arrives holding an id copied from somewhere else, and failing to find
 * it would be the one surprising miss.
 */
function haystack(entry: AuditLogEntrySummary, timeZone: TimeZoneMode): string {
  const isJob = entry.http_method === NON_REQUEST_SENTINEL;
  return [
    formatTimestamp(entry.occurred_at, timeZone),
    entry.occurred_at,
    entry.actor_display,
    entry.actor_role_at_time,
    entry.action,
    entry.entity_type,
    entry.entity_id,
    entry.outcome,
    OUTCOME_LABEL[entry.outcome],
    isJob ? BACKGROUND_JOB : entry.source_ip,
    entry.request_id,
    entry.id,
  ]
    .filter((field): field is string => Boolean(field))
    .join(' ')
    .toLowerCase();
}

/** The search terms in a raw input, or an empty list when it searches nothing. */
export function searchTerms(text: string): string[] {
  return text.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** Whether one row satisfies every term. */
export function matchesSearch(
  entry: AuditLogEntrySummary,
  terms: readonly string[],
  timeZone: TimeZoneMode,
): boolean {
  if (terms.length === 0) return true;
  const searchable = haystack(entry, timeZone);
  return terms.every((term) => searchable.includes(term));
}

/** The loaded entries a search leaves. Returns the input untouched when empty. */
export function applySearch(
  entries: readonly AuditLogEntrySummary[],
  text: string,
  timeZone: TimeZoneMode,
): AuditLogEntrySummary[] {
  const terms = searchTerms(text);
  if (terms.length === 0) return entries as AuditLogEntrySummary[];
  return entries.filter((entry) => matchesSearch(entry, terms, timeZone));
}
